import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useReactiveVar } from '@apollo/client'
import { handleFetchMore } from '@sketch/components'
import {
  GetNotificationsDocument,
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
  NotificationListInfoFragment,
  useGetNotificationsQuery,
} from '@sketch/gql-types'
import { useDebounceValue, useOnEvent } from '@sketch/utils'

import { notificationsQueryOptions } from '../../graphql'
import { useInboxSearchContext } from './InboxSearchContext'
import { pinOrderVar } from './pinOrder'
import { buildSearchString } from './searchUtils'

// `userReceivedNewNotification` is dispatched by the bell-notifications
// subscription handler in cloud-frontend. Re-declaring the same field here so
// `@sketch/user`'s TS project sees it — declaration merging picks up the
// matching shape from cloud-frontend at runtime.
declare module '@sketch/utils' {
  export interface EventsMap {
    userReceivedNewNotification: NotificationListInfoFragment
  }
}

const DAY_MS = 24 * 60 * 60 * 1000
const SEARCH_DEBOUNCE_MS = 300

const ENTRIES_PATH = ['me', 'notifications', 'entries']

// Inlined to avoid pulling @sketch/graphql-cache into @sketch/user just for
// pagination dedupe. Notifications always have an identifier.
const notificationDataId = (o: { __typename?: string; identifier?: string }) =>
  o.identifier ? `${o.__typename}:${o.identifier}` : undefined

type TimeBucket = 'today' | 'this-week' | 'older'
type Bucket = 'pinned' | TimeBucket

const BUCKET_LABELS: Record<Bucket, string> = {
  pinned: 'Pinned',
  today: 'Today',
  'this-week': 'This Week',
  older: 'Older',
}

const getBucket = (createdAt: string): TimeBucket => {
  const diffDays = (Date.now() - new Date(createdAt).getTime()) / DAY_MS
  if (diffDays < 1) return 'today'
  if (diffDays < 7) return 'this-week'
  return 'older'
}

export type InboxItem = {
  id: string
  typename: string | undefined
  notification: NotificationListInfoFragment
  state: 'unread' | 'read'
  title: string | null
  subtitle: string | null
  createdAt: string
}

export type InboxGroup = { id: Bucket; label: string; items: InboxItem[] }

export const useInboxNotifications = () => {
  const { search, options, sort } = useInboxSearchContext()
  const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE_MS)
  const apolloClient = useApolloClient()

  const searchString = buildSearchString({ search: debouncedSearch, options })

  const queryVariables: GetNotificationsQueryVariables = useMemo(
    () => ({
      filter: {
        search: searchString || undefined,
        // The "Unread" filter is a state filter (not a `type:` search token),
        // so it narrows the queried `states` rather than the search string.
        states: options.unreadOnly ? ['UNREAD'] : ['UNREAD', 'DONE', 'READ'],
      },
      sortOrder: sort === 'newest' ? 'CREATED_AT_DESC' : 'CREATED_AT_ASC',
    }),
    [searchString, sort, options.unreadOnly]
  )

  const { data, fetchMore, loading, error } = useGetNotificationsQuery({
    ...notificationsQueryOptions,
    variables: queryVariables,
  })

  const pinOrder = useReactiveVar(pinOrderVar)

  const entries = data?.me?.notifications?.entries ?? []
  const after = data?.me?.notifications?.meta?.after ?? null

  /**
   * Count of new notifications that have been silently prepended to the
   * cache since the consumer last reset. Consumers (e.g. InboxList) decide
   * when to surface this — typically only when the user has scrolled away
   * from the top — and call `resetNewNotificationsCount` to clear it.
   */
  const [newNotificationsCount, setNewNotificationsCount] = useState(0)

  const resetNewNotificationsCount = useCallback(() => {
    setNewNotificationsCount(0)
  }, [])

  // Reset the count when the active query changes (search or sort) — the
  // previous count refers to the previous list.
  useEffect(() => {
    setNewNotificationsCount(0)
  }, [queryVariables])

  /**
   * Silently prepend new notifications to the cached query when the
   * `notificationCreated` subscription fires, and bump the counter so
   * consumers can surface a "new notifications" indicator.
   *
   * Variables are read through a ref so the event handler stays referentially
   * stable across renders — otherwise `useOnEvent` would resubscribe every
   * render because its callback dep would change.
   */
  const queryVariablesRef = useRef(queryVariables)
  queryVariablesRef.current = queryVariables

  // Prepend a new notification into the cached query for `variables`, deduping
  // against what's already there. Returns whether it actually inserted.
  const prependToCache = useCallback(
    (
      newNotification: NotificationListInfoFragment,
      variables: GetNotificationsQueryVariables
    ) => {
      let didPrepend = false

      apolloClient.cache.updateQuery<
        GetNotificationsQuery,
        GetNotificationsQueryVariables
      >({ query: GetNotificationsDocument, variables }, existing => {
        if (!existing?.me?.notifications) return existing

        const alreadyPresent = existing.me.notifications.entries.some(
          entry => entry.identifier === newNotification.identifier
        )

        if (alreadyPresent) return existing

        didPrepend = true
        return {
          ...existing,
          me: {
            ...existing.me,
            notifications: {
              ...existing.me.notifications,
              entries: [newNotification, ...existing.me.notifications.entries],
            },
          },
        }
      })

      return didPrepend
    },
    [apolloClient]
  )

  const handleNewNotification = useCallback(
    async (newNotification: NotificationListInfoFragment) => {
      const variables = queryVariablesRef.current

      // The "new notifications" badge surfaces newest-first (scroll up to the
      // top of the list), so the optimistic prepend only makes sense for
      // CREATED_AT_DESC. When sorting oldest-first the arrival belongs at the
      // end of the list — let the next refetch place it correctly.
      if (variables.sortOrder !== 'CREATED_AT_DESC') return

      // When a search or filter is active the visible list is filtered
      // server-side (via the serialized `search` token string — free text plus
      // `type:`/`workspace:`/date tokens), and the token grammar is parsed by
      // the backend, so only the server can say whether the arrival belongs in
      // the current view. Probe the filtered first page with `no-cache` (which
      // leaves the paginated main query untouched) and bail unless the arrival
      // shows up there. A brand-new notification sorts newest-first, so a real
      // match always lands on the first page.
      if (variables.filter?.search) {
        try {
          const { data } = await apolloClient.query<
            GetNotificationsQuery,
            GetNotificationsQueryVariables
          >({
            query: GetNotificationsDocument,
            variables,
            fetchPolicy: 'no-cache',
            // Match the main query's `errorPolicy: 'all'` (see
            // `notificationsQueryOptions`): a notification the user lost share
            // access to returns FORBIDDEN, and without this the probe would
            // reject and silently drop a genuinely-matching arrival.
            errorPolicy: 'all',
          })

          const matchesFilter = (data?.me?.notifications?.entries ?? []).some(
            entry => entry.identifier === newNotification.identifier
          )
          if (!matchesFilter) return
        } catch {
          // If the match check fails we can't safely decide — skip the
          // optimistic insert and let the next refetch place it correctly.
          return
        }

        // The active query may have changed while the probe was in flight
        // (the user re-filtered or re-sorted). The query we matched against is
        // no longer on screen, so don't insert into it or bump the live count.
        if (queryVariablesRef.current !== variables) return
      }

      if (prependToCache(newNotification, variables)) {
        setNewNotificationsCount(count => count + 1)
      }
    },
    [apolloClient, prependToCache]
  )

  useOnEvent('userReceivedNewNotification', handleNewNotification)

  const toItem = (entry: (typeof entries)[number]): InboxItem => ({
    id: entry.identifier,
    typename: entry.__typename,
    state: entry.state === 'UNREAD' ? 'unread' : 'read',
    title: entry.title,
    subtitle: entry.subtitle,
    createdAt: entry.createdAt,
    notification: entry,
  })

  const timeBuckets =
    sort === 'newest'
      ? (['today', 'this-week', 'older'] as const)
      : (['older', 'this-week', 'today'] as const)

  const groups: InboxGroup[] = []

  // Pinned notifications are pulled out of their time buckets and shown first
  // under a dedicated "Pinned" header, regardless of sort order. The backend
  // only exposes `isPinned` (no pin timestamp), so the most-recently-pinned-
  // first order is tracked client-side in `pinOrderVar`, updated when the user
  // pins/unpins. Entries with no recorded order (already pinned on load) keep
  // the backend's order beneath any pinned this session.
  const pinnedEntries = entries
    .filter(entry => entry.isPinned)
    .sort(
      (a, b) => (pinOrder[b.identifier] ?? 0) - (pinOrder[a.identifier] ?? 0)
    )
  if (pinnedEntries.length > 0) {
    groups.push({
      id: 'pinned',
      label: BUCKET_LABELS.pinned,
      items: pinnedEntries.map(toItem),
    })
  }

  timeBuckets.forEach(bucket => {
    const bucketEntries = entries.filter(
      entry => !entry.isPinned && getBucket(entry.createdAt) === bucket
    )
    if (bucketEntries.length === 0) return
    groups.push({
      id: bucket,
      label: BUCKET_LABELS[bucket],
      items: bucketEntries.map(toItem),
    })
  })

  const loadMore = useMemo(
    () =>
      handleFetchMore(fetchMore, ENTRIES_PATH, {
        dataIdFromObject: notificationDataId,
        after,
      }),
    [fetchMore, after]
  )

  return {
    loading,
    error,
    groups,
    loadMore,
    after,
    newNotificationsCount,
    resetNewNotificationsCount,
  }
}
